import axios from 'axios';

import { COREAPI, getUserData } from '../constants/global';
import { type ApiResult, classifyResponse } from './api-result';

/**
 * Chunked upload through api.core's MediaStash relay — shared by result videos and
 * cancellation evidence. The browser never talks to MediaStash: api.core opens the session on
 * `sign`, takes each chunk on `PUT …/upload/{uploadId}/chunks/{index}`, and assembles on
 * `complete`. Every route here is relative to a feature base, e.g.
 * `/api/v1/Election/{e}/result-videos` or `/api/v1/Election/{e}/cancellations/{id}/evidence`.
 *
 * Same discipline as the other `src/lib` clients: no 401→logout interceptor, and every status
 * resolves so callers classify instead of catching.
 */
const relayApi = axios.create({ baseURL: COREAPI, timeout: 20_000 });

relayApi.interceptors.request.use((config) => {
	const { token, user } = getUserData();
	if (token) config.headers.Authorization = `Bearer ${token}`;
	if (user?.PartyId) config.headers.PartyId = user.PartyId;
	return config;
});

relayApi.interceptors.response.use((response) => {
	if (typeof response.data === 'string' && response.data) {
		try {
			response.data = JSON.parse(response.data);
		} catch {
			// Not JSON — leave as-is.
		}
	}
	return response;
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const RELAY_CODES = {
	storageUnavailable: 'STORAGE_UNAVAILABLE',
	storagePending: 'STORAGE_PENDING',
	/** Client-side: a sign response without `provider: 'mediastash'` — the server isn't on the relay yet. */
	storageNotReady: 'STORAGE_NOT_READY',
	invalidRange: 'INVALID_RANGE',
	notYours: 'UPLOAD_NOT_YOURS',
	sessionNotFound: 'UPLOAD_SESSION_NOT_FOUND',
	sessionExpired: 'UPLOAD_SESSION_EXPIRED',
	rateLimited: 'RATE_LIMITED',
	chunksMissing: 'CHUNKS_MISSING',
	hashMismatch: 'HASH_MISMATCH',
	mediaRejected: 'MEDIA_REJECTED',
} as const;

/** Codes that mean "the server can't store media right now" — the bytes wait on the phone. */
export const STORAGE_PAUSED_CODES: readonly string[] = [RELAY_CODES.storageUnavailable, RELAY_CODES.storageNotReady];

export const isStoragePaused = (code?: string): boolean => !!code && STORAGE_PAUSED_CODES.includes(code);

/** The `sign` response — an open upload session on api.core. */
export interface IRelaySession {
	provider: 'mediastash';
	uploadId: string;
	chunkSize: number;
	totalChunks: number;
	storagePublicId: string;
	expiresAt: string;
	windowClosesAt?: string;
	/** An asset with this clientUploadId already exists — skip straight to register. */
	alreadyStored: boolean;
}

export interface IRelaySessionState {
	chunkSize: number;
	totalChunks: number;
	receivedChunks: number[];
	expiresAt: string;
}

export interface IRelayComplete {
	storagePublicId: string;
	pending: boolean;
	bytes: number;
}

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const positiveInt = (v: unknown): number | undefined => (Number.isInteger(v) && (v as number) > 0 ? (v as number) : undefined);

/**
 * Reads a `sign` payload. A 2xx without `provider: 'mediastash'` is an api.core that still
 * signs for the retired provider (or a half-rolled one): retry later, keep the bytes.
 */
export const toRelaySessionResult = (status: number, body: unknown): ApiResult<IRelaySession> => {
	if (status >= 200 && status < 300) {
		const data = isObject(body) ? body.data : undefined;
		if (!isObject(data) || data.provider !== 'mediastash') {
			return { kind: 'retry', status, code: RELAY_CODES.storageNotReady };
		}
	}
	return classifyResponse(status, body, (data) => {
		if (!isObject(data) || typeof data.uploadId !== 'string' || typeof data.storagePublicId !== 'string') return undefined;
		const chunkSize = positiveInt(data.chunkSize);
		const totalChunks = positiveInt(data.totalChunks);
		if (!chunkSize || !totalChunks || typeof data.expiresAt !== 'string') return undefined;
		return {
			provider: 'mediastash',
			uploadId: data.uploadId,
			chunkSize,
			totalChunks,
			storagePublicId: data.storagePublicId,
			expiresAt: data.expiresAt,
			windowClosesAt: typeof data.windowClosesAt === 'string' ? data.windowClosesAt : undefined,
			alreadyStored: data.alreadyStored === true,
		};
	});
};

export const toSessionStateResult = (status: number, body: unknown): ApiResult<IRelaySessionState> =>
	classifyResponse(status, body, (data) => {
		if (!isObject(data) || !Array.isArray(data.receivedChunks)) return undefined;
		const chunkSize = positiveInt(data.chunkSize);
		const totalChunks = positiveInt(data.totalChunks);
		if (!chunkSize || !totalChunks) return undefined;
		return {
			chunkSize,
			totalChunks,
			receivedChunks: data.receivedChunks.filter((n): n is number => Number.isInteger(n)),
			expiresAt: typeof data.expiresAt === 'string' ? data.expiresAt : '',
		};
	});

/** True when a stored session can still take chunks: right provider and not about to expire. */
export const sessionUsable = (session: IRelaySession | undefined | null, now: number, marginMs = 60_000): boolean =>
	!!session && session.provider === 'mediastash' && !!session.uploadId && Date.parse(session.expiresAt) - now > marginMs;

// ---------------------------------------------------------------------------
// Outcomes
// ---------------------------------------------------------------------------

export type ChunkOutcome =
	| { kind: 'progress'; index: number }
	| { kind: 'offline' }
	/** The same chunk may be accepted later. `retryAfterMs` comes from `Retry-After`. */
	| { kind: 'retry'; code?: string; retryAfterMs?: number }
	/** The session is dead (expired, unknown, bad range) — re-sign and start over. */
	| { kind: 'restart'; code?: string }
	/** Sending again cannot succeed (another user's session, media refused). */
	| { kind: 'refused'; code?: string; status: number };

export type CompleteOutcome =
	| { kind: 'done'; data: IRelayComplete }
	| { kind: 'missing'; missing: number[] }
	| { kind: 'offline' }
	| { kind: 'retry'; code?: string; retryAfterMs?: number }
	| { kind: 'restart'; code?: string }
	| { kind: 'refused'; code?: string; status: number };

const readCode = (body: unknown): string | undefined =>
	isObject(body) && isObject(body.data) && typeof body.data.code === 'string'
		? body.data.code
		: isObject(body) && typeof body.code === 'string'
			? body.code
			: undefined;

/** `Retry-After` in seconds or as an HTTP date; anything else is ignored. */
export const parseRetryAfter = (value: unknown, now = Date.now()): number | undefined => {
	if (typeof value !== 'string' && typeof value !== 'number') return undefined;
	const seconds = Number(value);
	if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;
	const at = Date.parse(String(value));
	return Number.isFinite(at) ? Math.max(0, at - now) : undefined;
};

const isRetryStatus = (status: number) => status >= 500 || status === 408 || status === 429 || status === 401;

/**
 * Maps one chunk PUT's response. 401 retries rather than restarts: it is the agent's Core token,
 * not the session, and signing back in fixes it.
 */
export const toChunkOutcome = (status: number, body: unknown, index: number, retryAfter?: unknown): ChunkOutcome => {
	if (!status) return { kind: 'offline' };
	if (status >= 200 && status < 300) return { kind: 'progress', index };
	const code = readCode(body);
	if (isRetryStatus(status)) return { kind: 'retry', code, retryAfterMs: parseRetryAfter(retryAfter) };
	if (status === 403) return { kind: 'refused', code: code ?? RELAY_CODES.notYours, status };
	// 400 INVALID_RANGE, 404 / 410 session gone, 413 a chunk the session didn't expect.
	return { kind: 'restart', code };
};

export const toCompleteOutcome = (status: number, body: unknown, retryAfter?: unknown): CompleteOutcome => {
	if (!status) return { kind: 'offline' };
	const code = readCode(body);
	if (status === 409 && code === RELAY_CODES.chunksMissing) {
		const data = isObject(body) && isObject(body.data) ? body.data : {};
		const missing = Array.isArray(data.missing) ? data.missing.filter((n): n is number => Number.isInteger(n)) : [];
		return { kind: 'missing', missing };
	}
	if (status >= 200 && status < 300) {
		const res = classifyResponse(status, body, (data) =>
			isObject(data) && typeof data.storagePublicId === 'string'
				? { storagePublicId: data.storagePublicId, pending: data.pending === true, bytes: Number(data.bytes) || 0 }
				: undefined
		);
		return res.kind === 'ok' ? { kind: 'done', data: res.data } : { kind: 'retry' };
	}
	if (isRetryStatus(status)) return { kind: 'retry', code, retryAfterMs: parseRetryAfter(retryAfter) };
	if (status === 422 && code === RELAY_CODES.mediaRejected) return { kind: 'refused', code, status };
	if (status === 403) return { kind: 'refused', code: code ?? RELAY_CODES.notYours, status };
	// 422 HASH_MISMATCH, 404 / 410, an unknown 409 — the session can't finish; start over.
	return { kind: 'restart', code };
};

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

const all = () => true;

interface IRaw {
	status: number;
	body: unknown;
	retryAfter?: unknown;
}

const send = async (run: () => Promise<{ status: number; data: unknown; headers?: unknown }>): Promise<IRaw> => {
	try {
		const res = await run();
		const headers = res.headers as { get?: (k: string) => unknown; [k: string]: unknown } | undefined;
		const retryAfter = headers
			? typeof headers.get === 'function'
				? headers.get('retry-after')
				: headers['retry-after']
			: undefined;
		return { status: res.status, body: res.data, retryAfter };
	} catch {
		return { status: 0, body: undefined };
	}
};

/** Generous per-chunk timeout: ~20 KB/s floor, never under a minute. */
const chunkTimeoutMs = (bytes: number) => Math.max(60_000, Math.round((bytes / 20_000) * 1000));

/** `POST {base}/sign` with the feature's own body plus the relay fields. */
export const signRelay = async (base: string, body: Record<string, unknown>): Promise<ApiResult<IRelaySession>> => {
	const raw = await send(() => relayApi.post(`${base}/sign`, body, { validateStatus: all }));
	return toRelaySessionResult(raw.status, raw.body);
};

export const getRelaySession = async (base: string, uploadId: string): Promise<ApiResult<IRelaySessionState>> => {
	const raw = await send(() => relayApi.get(`${base}/upload/${encodeURIComponent(uploadId)}`, { validateStatus: all }));
	return toSessionStateResult(raw.status, raw.body);
};

/** The byte range chunk `index` covers at the session's chunk size, end-exclusive. */
export const chunkRange = (session: Pick<IRelaySession, 'chunkSize'>, total: number, index: number) => {
	const start = index * session.chunkSize;
	return { start, end: Math.min(start + session.chunkSize, total) };
};

/** One raw PUT of chunk `index`. Idempotent server-side — resending a received chunk is a 204. */
export const uploadRelayChunk = async (
	base: string,
	session: Pick<IRelaySession, 'uploadId' | 'chunkSize'>,
	blob: Blob,
	index: number,
	onProgress?: (loadedInChunk: number) => void
): Promise<ChunkOutcome> => {
	const { start, end } = chunkRange(session, blob.size, index);
	const raw = await send(() =>
		relayApi.put(
			`${base}/upload/${encodeURIComponent(session.uploadId)}/chunks/${index}`,
			blob.slice(start, end, 'application/octet-stream'),
			{
				validateStatus: all,
				timeout: chunkTimeoutMs(end - start),
				headers: { 'Content-Type': 'application/octet-stream', 'Content-Range': `bytes ${start}-${end - 1}/${blob.size}` },
				onUploadProgress: (event) => onProgress?.(event.loaded),
			}
		)
	);
	return toChunkOutcome(raw.status, raw.body, index, raw.retryAfter);
};

export const completeRelayUpload = async (base: string, uploadId: string): Promise<CompleteOutcome> => {
	// Assembly and the hash check can take well over the usual 20 s.
	const raw = await send(() =>
		relayApi.post(`${base}/upload/${encodeURIComponent(uploadId)}/complete`, undefined, {
			validateStatus: all,
			timeout: 180_000,
		})
	);
	return toCompleteOutcome(raw.status, raw.body, raw.retryAfter);
};

// ---------------------------------------------------------------------------
// The upload loop
// ---------------------------------------------------------------------------

export interface IRelayTransport {
	getSession: (uploadId: string) => Promise<ApiResult<IRelaySessionState>>;
	uploadChunk: (
		session: IRelaySession,
		blob: Blob,
		index: number,
		onProgress?: (loadedInChunk: number) => void
	) => Promise<ChunkOutcome>;
	complete: (uploadId: string) => Promise<CompleteOutcome>;
}

export type RelayUploadOutcome =
	| { kind: 'done'; data: IRelayComplete }
	| { kind: 'offline' }
	| { kind: 'retry'; code?: string; retryAfterMs?: number }
	| { kind: 'restart'; code?: string }
	| { kind: 'refused'; code?: string; status: number };

/** How many times a `CHUNKS_MISSING` answer is chased in one run before backing off. */
const MAX_COMPLETE_ROUNDS = 3;

/**
 * Sends every chunk the server doesn't have yet, then completes. Resumes from the server's
 * `receivedChunks`, never from a locally stored offset — the session map is the truth.
 * `onChunk` fires after each acknowledged chunk with the bytes the server now holds.
 */
export const runRelayUpload = async (
	session: IRelaySession,
	blob: Blob,
	transport: IRelayTransport,
	{ onChunk, onProgress }: { onChunk?: (uploadedBytes: number) => void; onProgress?: (percent: number) => void } = {}
): Promise<RelayUploadOutcome> => {
	const total = blob.size;
	const state = await transport.getSession(session.uploadId);
	if (state.kind === 'offline') return { kind: 'offline' };
	if (state.kind === 'retry') return { kind: 'retry', code: state.code };
	if (state.kind !== 'ok') {
		if (state.kind === 'rejected' && state.status === 401) return { kind: 'retry', code: state.code };
		if (state.kind === 'rejected' && state.status === 403) return { kind: 'refused', code: state.code, status: 403 };
		return { kind: 'restart', code: state.code };
	}

	const received = new Set(state.data.receivedChunks);
	const bytesOf = (i: number) => {
		const { start, end } = chunkRange(session, total, i);
		return end - start;
	};
	let uploaded = [...received].reduce((sum, i) => (i < session.totalChunks ? sum + bytesOf(i) : sum), 0);

	const sendChunks = async (indices: number[]): Promise<RelayUploadOutcome | null> => {
		for (const index of indices) {
			const before = uploaded;
			const out = await transport.uploadChunk(session, blob, index, (loaded) =>
				onProgress?.(Math.min(99, Math.round(((before + loaded) / Math.max(total, 1)) * 100)))
			);
			if (out.kind !== 'progress') return out;
			if (!received.has(index)) {
				received.add(index);
				uploaded += bytesOf(index);
			}
			onChunk?.(uploaded);
		}
		return null;
	};

	const every = Array.from({ length: session.totalChunks }, (_, i) => i);
	const first = await sendChunks(every.filter((i) => !received.has(i)));
	if (first) return first;

	for (let round = 0; round < MAX_COMPLETE_ROUNDS; round += 1) {
		const done = await transport.complete(session.uploadId);
		if (done.kind === 'done') {
			onProgress?.(100);
			return done;
		}
		if (done.kind !== 'missing') return done;
		// The server lost track of some chunks — resend exactly those, then complete again.
		done.missing.forEach((i) => received.delete(i));
		const again = await sendChunks(done.missing.filter((i) => i >= 0 && i < session.totalChunks));
		if (again) return again;
	}
	return { kind: 'retry', code: RELAY_CODES.chunksMissing };
};

/** The live transport for one feature base. */
export const relayTransport = (base: string): IRelayTransport => ({
	getSession: (uploadId) => getRelaySession(base, uploadId),
	uploadChunk: (session, blob, index, onProgress) => uploadRelayChunk(base, session, blob, index, onProgress),
	complete: (uploadId) => completeRelayUpload(base, uploadId),
});

// ---------------------------------------------------------------------------
// Hashing + ids
// ---------------------------------------------------------------------------

/** Hashing reads the whole file into memory; past this, skip it (the server treats sha256 as optional). */
export const MAX_HASH_BYTES = 80 * 1024 * 1024;

/** Lower-case hex SHA-256 via `crypto.subtle`, or undefined where it isn't available. */
export const sha256Hex = async (blob: Blob): Promise<string | undefined> => {
	try {
		if (blob.size > MAX_HASH_BYTES || typeof crypto === 'undefined' || !crypto.subtle) return undefined;
		const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
		return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
	} catch {
		return undefined;
	}
};

/** A v4 UUID for `clientUploadId` — the server's idempotency key for the asset. */
export const newClientUploadId = (): string => {
	try {
		if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
	} catch {
		// Fall through.
	}
	const hex = Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16));
	hex[12] = '4';
	hex[16] = ((parseInt(hex[16], 16) & 0x3) | 0x8).toString(16);
	const s = hex.join('');
	return `${s.slice(0, 8)}-${s.slice(8, 12)}-${s.slice(12, 16)}-${s.slice(16, 20)}-${s.slice(20)}`;
};
